import { supabase } from "./supabase";
import { trackEvent } from "./analytics";
import { normalizeGameConfig, normalizeQuizTheme } from "./quizTheme";
import type { Activity, AvatarConfig, AvatarStyle, Difficulty, DraftQuestion, ExperienceMode, ForgeQuestion, GameConfig, GameReaction, GameState, HostGame, ParticipantSession, ProfileTheme, PublicActivity, QuizTheme, Role, SessionResponse, SessionUser, StudentConstellation, TeacherRadar, CreatorProfile, SharedForgeQuestion, PlayerProfile, CosmeticsState, AdminDashboard, AdminUserRow, AdminAnnouncement, FeatureFlag, TeacherInviteAdmin, AdminAuditEntry, CosmeticCatalogItem, AdminActivityRow, StudyState, StudyAttemptResult, LuaIdManifest, ContactPayload, AdminContactMessage, RecordsProfile, NewLifeResult, UserNotification } from "./types";

class ApiError extends Error {
  status: number;
  code?: string;
  constructor(message: string, status = 400, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

const messageFor = (message?: string) => {
  if (!message) return "Não foi possível concluir a ação.";
  const known: Record<string, string> = {
    INVALID_TEACHER_CODE: "O código de professor não é válido.",
    TEACHER_REQUIRED: "Esta ação exige uma conta de professor.",
    GAME_NOT_FOUND: "Sala não encontrada.",
    GAME_ALREADY_STARTED: "Essa partida já começou.",
    GAME_NOT_STARTABLE: "A partida não pode ser iniciada agora.",
    GAME_NOT_RUNNING: "A partida não está em andamento.",
    INVALID_PARTICIPANT: "Sua entrada na sala expirou. Entre novamente.",
    ACTIVITY_NOT_FOUND: "Atividade não encontrada.",
    AUTH_REQUIRED: "Entre na sua conta para continuar.",
    INVALID_AVATAR_STYLE: "Esse estilo de avatar não está disponível.",
    INVALID_PROFILE_TITLE: "Escolha um título de perfil válido.",
    BIO_TOO_LONG: "A bio pode ter até 220 caracteres.",
    PROFILE_TEXT_NOT_ALLOWED: "Esse texto não combina com um espaço escolar. Ajuste o nome, título ou bio e tente de novo.",
    AVATAR_AI_UNAVAILABLE: "A IA de avatar não respondeu agora. Tenta de novo já já.",
    AVATAR_AI_INVALID_RESPONSE: "A IA respondeu sem uma imagem válida. Tenta gerar novamente.",
    POLLINATIONS_KEY_REQUIRED: "Falta configurar a chave da Pollinations no Cloudflare.",
    POLLINATIONS_KEY_INVALID: "A chave da Pollinations foi recusada. Confira a variável no Cloudflare.",
    POLLINATIONS_BALANCE_REQUIRED: "A conta Pollinations está sem Pollen suficiente para esse modelo.",
    POLLINATIONS_RATE_LIMIT: "Muita geração ao mesmo tempo. Espera um pouquinho e tenta de novo.",
    INVALID_EXPERIENCE_MODE: "Escolha um formato de atividade válido.",
    ADMIN_REQUIRED: "Esta área é exclusiva da gestão da Rede Lua.",
    CANNOT_SUSPEND_SELF: "Você não pode suspender sua própria conta administrativa.",
    ADMIN_ROLE_PROTECTED: "Contas administrativas são gerenciadas separadamente.",
    SUPER_ADMIN_PROTECTED: "A conta principal da gestão não pode ser suspensa.",
    INVITE_CODE_TOO_SHORT: "Use um código de professor com pelo menos 8 caracteres.",
    FLAG_NOT_FOUND: "Esse experimento não existe mais.",
    COSMETIC_NOT_FOUND: "Esse item da Loja Lunar não foi encontrado.",
    COSMETIC_LEVEL_REQUIRED: "Você ainda não chegou ao nível necessário para esse item.",
    NOT_ENOUGH_MOON_COINS: "Você ainda não tem Luas suficientes para esse item.",
    LIFE_NOT_READY: "A vida atual ainda não atingiu o XP necessário para renascer.",
  };
  return known[message] || message.replace(/^.*?:\s*/, "") || "Não foi possível concluir a ação.";
};

function dbError(error: any): never {
  const raw = String(error?.message || error?.error_description || "");
  const code = Object.keys({
    INVALID_TEACHER_CODE: 1, TEACHER_REQUIRED: 1, GAME_NOT_FOUND: 1, GAME_ALREADY_STARTED: 1,
    GAME_NOT_STARTABLE: 1, GAME_NOT_RUNNING: 1, INVALID_PARTICIPANT: 1, ACTIVITY_NOT_FOUND: 1,
    AUTH_REQUIRED: 1, INVALID_AVATAR_STYLE: 1, INVALID_PROFILE_TITLE: 1, BIO_TOO_LONG: 1, INVALID_EXPERIENCE_MODE: 1,
    ADMIN_REQUIRED: 1, CANNOT_SUSPEND_SELF: 1, ADMIN_ROLE_PROTECTED: 1, SUPER_ADMIN_PROTECTED: 1, INVITE_CODE_TOO_SHORT: 1,
    FLAG_NOT_FOUND: 1, COSMETIC_NOT_FOUND: 1, COSMETIC_LEVEL_REQUIRED: 1, NOT_ENOUGH_MOON_COINS: 1, PROFILE_TEXT_NOT_ALLOWED: 1, LIFE_NOT_READY: 1, AVATAR_AI_UNAVAILABLE: 1,
  }).find((key) => raw.includes(key));
  throw new ApiError(messageFor(code || raw), 400, code);
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    headers: { "content-type": "application/json", ...(init?.headers || {}) },
    ...init,
  });
  const raw = await response.text();
  let body: any = {};
  try { body = raw ? JSON.parse(raw) : {}; } catch { body = {}; }
  if (!response.ok) {
    const fallback = raw && !raw.trim().startsWith("<") ? raw.slice(0, 220) : `Erro do servidor (${response.status}).`;
    throw new ApiError(body.message || fallback || "Não foi possível concluir a ação.", response.status, body.error);
  }
  return body as T;
}

async function profileFor(user: { id: string; email?: string | null }): Promise<SessionUser> {
  const { data, error } = await supabase.from("rede_lua_profiles")
    .select("display_name,role,xp,level,streak_days,bio,profile_title,avatar_style,avatar_seed,avatar_config,profile_theme,profile_visibility,favorite_subjects,moon_coins,account_status,profile_handle,life_number,life_xp,legacy_stars,best_level,avatar_ai_prompt,avatar_ai_image_url,avatar_ai_seed,avatar_ai_generated_at")
    .eq("user_id", user.id)
    .single();
  if (error) dbError(error);
  return {
    id: user.id,
    email: user.email || "",
    displayName: data.display_name,
    role: data.role as Role,
    xp: Number(data.xp || 0),
    level: Number(data.level || 1),
    streakDays: Number(data.streak_days || 0),
    bio: data.bio || "",
    profileTitle: data.profile_title || "Explorador Lunar",
    avatarStyle: (data.avatar_style || "adventurer") as AvatarStyle,
    avatarSeed: data.avatar_seed || user.id,
    avatarConfig: (data.avatar_config || {}) as AvatarConfig,
    profileTheme: ({ accent: "#ffbd2e", surface: "#0b2d68", background: "#071c45", card: "#ffffff", pattern: "stars", ...(data.profile_theme || {}) }) as ProfileTheme,
    profileVisibility: (data.profile_visibility || "private") as "private" | "classroom",
    favoriteSubjects: Array.isArray(data.favorite_subjects) ? data.favorite_subjects : [],
    moonCoins: Number(data.moon_coins || 0),
    accountStatus: (data.account_status || "active") as "active" | "suspended",
    profileHandle: data.profile_handle || undefined,
    lifeNumber: Number(data.life_number || 1),
    lifeXp: Number(data.life_xp || 0),
    legacyStars: Number(data.legacy_stars || 0),
    bestLevel: Number(data.best_level || data.level || 1),
  };
}

async function authHeaders() {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new ApiError("Entre na sua conta para continuar.", 401, "AUTH_REQUIRED");
  return { Authorization: `Bearer ${token}` };
}

async function claimTeacher(code: string) {
  const { data, error } = await supabase.rpc("rede_lua_claim_teacher", { p_code: code });
  if (error) dbError(error);
  if (!data) throw new ApiError("O código de professor não é válido.", 403, "INVALID_TEACHER_CODE");
}

function mapActivity(value: any): Activity {
  return {
    id: value.id,
    title: value.title,
    subject: value.subject,
    description: value.description || "",
    status: value.status,
    difficulty: (value.difficulty || "medium") as Difficulty,
    tags: Array.isArray(value.tags) ? value.tags : [],
    questionCount: Number(value.questionCount ?? value.question_count ?? 0),
    experienceMode: (value.experienceMode ?? value.experience_mode ?? "classic") as ExperienceMode,
    theme: normalizeQuizTheme(value.theme ?? value.theme_config),
    gameConfig: normalizeGameConfig(value.gameConfig ?? value.game_config),
    createdAt: Number(value.createdAt ?? (value.created_at ? new Date(value.created_at).getTime() : Date.now())),
    updatedAt: Number(value.updatedAt ?? (value.updated_at ? new Date(value.updated_at).getTime() : Date.now())),
  };
}

function mapPublicActivity(value: any): PublicActivity {
  return { ...mapActivity(value), score: value.score == null ? undefined : Number(value.score) };
}

function mapRadar(data: any): TeacherRadar {
  const summary = data?.summary || {};
  return {
    summary: { games: Number(summary.games || 0), participants: Number(summary.participants || 0), answers: Number(summary.answers || 0), accuracy: Number(summary.accuracy || 0) },
    hotspots: (data?.hotspots || []).map((item: any) => ({ questionId: item.question_id, prompt: item.prompt, activityTitle: item.activity_title, subject: item.subject, totalAnswers: Number(item.total_answers || 0), wrongAnswers: Number(item.wrong_answers || 0), errorRate: Number(item.error_rate || 0) })),
    learners: (data?.learners || []).map((item: any) => ({ userId: item.user_id, displayName: item.display_name, subject: item.subject, masteryScore: Number(item.mastery_score || 0), confidence: Number(item.confidence || 0), attempts: Number(item.attempts || 0), signal: item.signal })),
    recentGames: (data?.recentGames || []).map((item: any) => ({ id: item.id, code: item.code, status: item.status, title: item.title, subject: item.subject, createdAt: item.created_at, endedAt: item.ended_at || null })),
  };
}

function mapConstellation(data: any): StudentConstellation {
  const profile = data?.profile || {};
  const today = data?.today || {};
  return {
    profile: { userId: profile.user_id, displayName: profile.display_name, xp: Number(profile.xp || 0), level: Number(profile.level || 1), streakDays: Number(profile.streak_days || 0) },
    mastery: (data?.mastery || []).map((item: any) => ({ subject: item.subject, masteryScore: Number(item.mastery_score || 0), confidence: Number(item.confidence || 0), attempts: Number(item.attempts || 0), correctCount: Number(item.correct_count || 0), lastResult: item.last_result ?? null, updatedAt: item.updated_at })),
    missions: (data?.missions || []).map((item: any) => ({ type: item.type, label: item.label, activityId: item.activity_id, title: item.title, subject: item.subject, description: item.description || "", difficulty: (item.difficulty || "medium") as Difficulty, target: Number(item.target || 0), progress: Number(item.progress || 0), rewardXp: Number(item.reward_xp || 0) })),
    today: { answers: Number(today.answers || 0), correct: Number(today.correct || 0), accuracy: Number(today.accuracy || 0) },
  };
}

export const api = {
  session: async (): Promise<SessionResponse> => {
    const { data, error } = await supabase.auth.getSession();
    if (error || !data.session?.user) return { authenticated: false, user: null };
    const pendingCode = localStorage.getItem("rede-lua-pending-teacher-code");
    if (pendingCode) {
      try { await claimTeacher(pendingCode); localStorage.removeItem("rede-lua-pending-teacher-code"); }
      catch { /* deixa o código salvo para o usuário tentar novamente */ }
    }
    try { return { authenticated: true, user: await profileFor(data.session.user) }; }
    catch (error) { throw error instanceof Error ? error : new ApiError("Não foi possível carregar o perfil."); }
  },

  login: async (payload: { email: string; password: string; teacherCode?: string }) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email: payload.email.trim(), password: payload.password });
    if (error || !data.user) throw new ApiError(error?.message || "E-mail ou senha incorretos.", 401, "INVALID_CREDENTIALS");
    const pendingCode = payload.teacherCode?.trim() || localStorage.getItem("rede-lua-pending-teacher-code");
    if (pendingCode) {
      await claimTeacher(pendingCode);
      localStorage.removeItem("rede-lua-pending-teacher-code");
    }
    trackEvent("auth", "login");
    return { ok: true as const, user: await profileFor(data.user) };
  },

  activateTeacher: async (code: string) => {
    if (!code.trim()) throw new ApiError("Digite o código de professor.", 400);
    await claimTeacher(code.trim());
    const { data } = await supabase.auth.getUser();
    if (!data.user) throw new ApiError("Entre novamente na sua conta.", 401);
    trackEvent("auth", "activate_teacher");
    return { ok: true as const, user: await profileFor(data.user) };
  },

  register: async (payload: { displayName: string; email: string; password: string; role: Exclude<Role, "admin">; teacherCode?: string }) => {
    if (payload.role === "teacher" && !payload.teacherCode?.trim()) throw new ApiError("Digite o código de professor.", 400);
    const { data, error } = await supabase.auth.signUp({
      email: payload.email.trim(),
      password: payload.password,
      options: { data: { display_name: payload.displayName.trim() } },
    });
    if (error) throw new ApiError(error.message, 400, (error as any).code);
    if (!data.user) throw new ApiError("Não foi possível criar a conta.", 400);

    if (payload.role === "teacher" && payload.teacherCode) {
      if (data.session) await claimTeacher(payload.teacherCode.trim());
      else localStorage.setItem("rede-lua-pending-teacher-code", payload.teacherCode.trim());
    }
    trackEvent("auth", "register", payload.role);
    return {
      ok: true as const,
      user: data.session ? await profileFor(data.user) : null,
      requiresConfirmation: !data.session,
      teacherPending: payload.role === "teacher" && !data.session,
    };
  },

  logout: async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw new ApiError(error.message);
    return { ok: true as const };
  },

  updateProfile: async (payload: { displayName: string; bio: string; profileTitle: string; avatarStyle: AvatarStyle; avatarSeed: string; avatarConfig: AvatarConfig; profileTheme: ProfileTheme; visibility: "private" | "classroom"; favoriteSubjects: string[] }) => {
    const { data, error } = await supabase.rpc("rede_lua_update_profile_studio", {
      p_display_name: payload.displayName, p_bio: payload.bio, p_profile_title: payload.profileTitle,
      p_avatar_style: payload.avatarStyle, p_avatar_seed: payload.avatarSeed, p_avatar_config: payload.avatarConfig,
      p_profile_theme: payload.profileTheme, p_visibility: payload.visibility, p_favorite_subjects: payload.favoriteSubjects.slice(0, 8),
    });
    if (error) dbError(error);
    trackEvent("profile", "save", payload.avatarStyle);
    return { ok: true as const, profile: data };
  },

  uploadEducationImage: async (file: File, folder: "themes" | "questions" = "themes") => {
    if (!['image/png','image/jpeg','image/webp'].includes(file.type)) throw new ApiError("Use PNG, JPG ou WebP.");
    if (file.size > 3 * 1024 * 1024) throw new ApiError("A imagem pode ter no máximo 3 MB.");
    const { data: auth } = await supabase.auth.getSession();
    const uid = auth.session?.user.id;
    if (!uid) throw new ApiError("Entre na sua conta para enviar imagens.", 401);
    const ext = file.type === 'image/png' ? 'png' : file.type === 'image/webp' ? 'webp' : 'jpg';
    const path = `${uid}/${folder}/${crypto.randomUUID()}.${ext}`;
    const { error } = await supabase.storage.from("rede-lua-assets").upload(path, file, { cacheControl: "31536000", upsert: false, contentType: file.type });
    if (error) dbError(error);
    const { data } = supabase.storage.from("rede-lua-assets").getPublicUrl(path);
    return { path, url: data.publicUrl };
  },

  myActivities: async () => {
    const { data, error } = await supabase.rpc("rede_lua_my_activities");
    if (error) dbError(error);
    return { activities: (Array.isArray(data) ? data : []).map(mapActivity) };
  },

  createActivity: async (payload: { title: string; subject: string; description: string; status: "draft" | "published"; difficulty: Difficulty; tags: string[]; questions: DraftQuestion[]; experienceMode: ExperienceMode; theme: QuizTheme; gameConfig: GameConfig }) => {
    const { data, error } = await supabase.rpc("rede_lua_create_activity_v3", {
      p_title: payload.title,
      p_subject: payload.subject,
      p_description: payload.description,
      p_status: payload.status,
      p_difficulty: payload.difficulty,
      p_tags: payload.tags,
      p_questions: payload.questions,
      p_experience_mode: payload.experienceMode,
      p_theme_config: payload.theme,
      p_game_config: payload.gameConfig,
    });
    if (error) dbError(error);
    const activity = mapActivity(data);
    trackEvent("teacher", "create_activity", activity.subject);
    return { ok: true as const, activity };
  },

  deleteActivity: async (id: string) => {
    const { data, error } = await supabase.rpc("rede_lua_remove_activity", { p_activity_id: id });
    if (error) dbError(error);
    return data as { ok: true; archived: boolean; message: string };
  },

  duplicateActivity: async (id: string) => {
    const { data, error } = await supabase.rpc("rede_lua_duplicate_activity", { p_activity_id: id });
    if (error) dbError(error);
    return data as { ok: true; id: string; title: string };
  },

  createGame: async (activityId: string) => {
    const { data, error } = await supabase.rpc("rede_lua_create_game", { p_activity_id: activityId });
    if (error) dbError(error);
    trackEvent("game", "create_room");
    return { ok: true as const, game: data as { id: string; code: string; status: string; activityTitle: string } };
  },
  hostGame: async (code: string) => {
    const { data, error } = await supabase.rpc("rede_lua_host_game_state", { p_code: code });
    if (error) dbError(error);
    return { game: data as HostGame };
  },
  startGame: async (code: string) => {
    const { data, error } = await supabase.rpc("rede_lua_start_game", { p_code: code });
    if (error) dbError(error);
    return data as { ok: true };
  },
  nextQuestion: async (code: string) => {
    const { data, error } = await supabase.rpc("rede_lua_next_question", { p_code: code });
    if (error) dbError(error);
    return data as { ok: true; finished: boolean; currentQuestion?: number };
  },
  joinGame: async (code: string, displayName: string) => {
    const { data, error } = await supabase.rpc("rede_lua_join_game", { p_code: code, p_display_name: displayName });
    if (error) dbError(error);
    trackEvent("game", "join_room");
    return data as { participant: ParticipantSession; game: { code: string; status: string } };
  },
  gameState: async (code: string, participant: ParticipantSession) => {
    const { data, error } = await supabase.rpc("rede_lua_game_state", { p_code: code, p_participant_id: participant.id, p_token: participant.token });
    if (error) dbError(error);
    return { game: data as GameState };
  },
  answer: async (code: string, participant: ParticipantSession, questionId: string, choiceIndex: number) => {
    const { data, error } = await supabase.rpc("rede_lua_answer", { p_code: code, p_participant_id: participant.id, p_token: participant.token, p_question_id: questionId, p_choice_index: choiceIndex });
    if (error) dbError(error);
    return data as { correct: boolean; awardedPoints: number; speedBonus: number; timedOut: boolean; explanation: string };
  },

  sendReaction: async (code: string, participant: ParticipantSession, reactionId: GameReaction["reactionId"]) => {
    const { data, error } = await supabase.rpc("rede_lua_send_reaction", { p_code: code, p_participant_id: participant.id, p_token: participant.token, p_reaction_id: reactionId });
    if (error) dbError(error);
    return data as { ok: true; accepted: boolean; throttled?: boolean; reactionId?: string };
  },
  gameReactions: async (code: string, participant: ParticipantSession) => {
    const { data, error } = await supabase.rpc("rede_lua_recent_reactions", { p_code: code, p_participant_id: participant.id, p_token: participant.token, p_limit: 12 });
    if (error) dbError(error);
    return { reactions: (Array.isArray(data) ? data : []) as GameReaction[] };
  },
  hostReactions: async (code: string) => {
    const { data, error } = await supabase.rpc("rede_lua_host_recent_reactions", { p_code: code, p_limit: 20 });
    if (error) dbError(error);
    return { reactions: (Array.isArray(data) ? data : []) as GameReaction[] };
  },

  searchActivities: async (q: string) => {
    const { data, error } = await supabase.rpc("rede_lua_search_activities", { p_query: q, p_limit: 12 });
    if (error) dbError(error);
    return { q, engine: "luacore" as const, activities: (Array.isArray(data) ? data : []).map(mapPublicActivity) };
  },
  recommendations: async () => {
    const { data: session } = await supabase.auth.getSession();
    const { data, error } = await supabase.rpc("rede_lua_recommend_activities", { p_limit: 8 });
    if (error) dbError(error);
    return { engine: "luacore" as const, personalized: Boolean(session.session?.user), activities: (Array.isArray(data) ? data : []).map(mapPublicActivity) };
  },
  trackActivity: async (activityId: string, eventType: "view" | "start" | "complete" | "like") => {
    const { data } = await supabase.auth.getSession();
    if (data.session) await supabase.rpc("rede_lua_record_activity_event", { p_activity_id: activityId, p_event_type: eventType });
    trackEvent("activity", eventType, undefined, activityId);
  },

  forgeQuestions: async (subject?: string) => {
    const { data, error } = await supabase.rpc("rede_lua_forge_questions", { p_subject: subject || null, p_limit: 40 });
    if (error) dbError(error);
    const questions: ForgeQuestion[] = (Array.isArray(data) ? data : []).map((item: any) => ({
      id: item.id, subject: item.subject, skillKey: item.skill_key, prompt: item.prompt,
      choices: Array.isArray(item.choices) ? item.choices : [], correctIndex: Number(item.correct_index || 0),
      explanation: item.explanation || "", difficulty: item.difficulty as Difficulty, tags: Array.isArray(item.tags) ? item.tags : [],
      timesUsed: Number(item.times_used || 0), shared: Boolean(item.shared), updatedAt: new Date(item.updated_at).getTime(),
    }));
    return { questions };
  },
  saveForgeQuestion: async (payload: { subject: string; skillKey: string; question: DraftQuestion; difficulty: Difficulty; tags?: string[] }) => {
    const { data, error } = await supabase.rpc("rede_lua_save_forge_question", {
      p_subject: payload.subject, p_skill_key: payload.skillKey, p_prompt: payload.question.prompt, p_choices: payload.question.choices,
      p_correct_index: payload.question.correctIndex, p_explanation: payload.question.explanation, p_difficulty: payload.difficulty, p_tags: payload.tags || [],
    });
    if (error) dbError(error);
    trackEvent("teacher", "forge_save", payload.subject);
    return { ok: true as const, id: data as string };
  },
  creatorProfile: async (): Promise<CreatorProfile> => {
    const { data, error } = await supabase.rpc("rede_lua_creator_profile");
    if (error) dbError(error);
    const stats = data?.stats || {};
    return {
      stats: { activities:Number(stats.activities||0), games:Number(stats.games||0), participants:Number(stats.participants||0), answers:Number(stats.answers||0), minutesPlayed:Number(stats.minutesPlayed||0), accuracy:Number(stats.accuracy||0), creatorXp:Number(stats.creatorXp||0), creatorLevel:Number(stats.creatorLevel||1) },
      badges: Array.isArray(data?.badges) ? data.badges : [],
      showcase: (Array.isArray(data?.showcase) ? data.showcase : []).map((item:any)=>({ ...item, plays:Number(item.plays||0), questions:Number(item.questions||0), theme:normalizeQuizTheme(item.theme), experienceMode:(item.experienceMode||"classic") as ExperienceMode, difficulty:(item.difficulty||"medium") as Difficulty })),
    };
  },
  sharedQuestions: async (subject?: string): Promise<{ questions: SharedForgeQuestion[] }> => {
    const { data, error } = await supabase.rpc("rede_lua_shared_forge", { p_subject: subject || null, p_limit: 30 });
    if (error) dbError(error);
    return { questions: (Array.isArray(data) ? data : []).map((item:any)=>({ id:item.id, subject:item.subject, skillKey:item.skill_key||"geral", prompt:item.prompt, choices:Array.isArray(item.choices)?item.choices:[], correctIndex:Number(item.correct_index||0), explanation:item.explanation||"", difficulty:(item.difficulty||"medium") as Difficulty, tags:Array.isArray(item.tags)?item.tags:[], timesUsed:Number(item.times_used||0), updatedAt:item.updated_at?new Date(item.updated_at).getTime():Date.now(), creatorName:item.creator_name||"Professor da Rede Lua", shared:true })) };
  },
  toggleQuestionShare: async (id: string, shared: boolean) => {
    const { error } = await supabase.rpc("rede_lua_set_forge_shared", { p_question_id:id, p_shared:shared });
    if (error) dbError(error);
    return { ok:true as const };
  },
  copySharedQuestion: async (id: string) => {
    const { data, error } = await supabase.rpc("rede_lua_copy_shared_forge", { p_question_id:id });
    if (error) dbError(error);
    return { ok:true as const, id:String(data) };
  },
  playerProfile: async (): Promise<PlayerProfile> => {
    const { data, error } = await supabase.rpc("rede_lua_player_profile");
    if (error) dbError(error);
    return { coins:Number(data?.coins||0), gamesCompleted:Number(data?.gamesCompleted||0), answers:Number(data?.answers||0), correct:Number(data?.correct||0), accuracy:Number(data?.accuracy||0), badges:Array.isArray(data?.badges)?data.badges:[], history:Array.isArray(data?.history)?data.history:[] };
  },
  myCosmetics: async (): Promise<CosmeticsState> => {
    const { data, error } = await supabase.rpc("rede_lua_my_cosmetics");
    if (error) dbError(error);
    return { coins:Number(data?.coins||0), unlocks:Array.isArray(data?.unlocks)?data.unlocks:[] };
  },
  unlockCosmetic: async (itemId:string) => {
    const { data, error } = await supabase.rpc("rede_lua_unlock_cosmetic", { p_item_id:itemId });
    if (error) dbError(error);
    return data as { ok:boolean; alreadyUnlocked?:boolean; coins:number; itemId?:string };
  },
  teacherRadar: async (days = 30) => {
    const { data, error } = await supabase.rpc("rede_lua_teacher_radar", { p_days: days });
    if (error) dbError(error);
    return mapRadar(data || {});
  },
  studentConstellation: async () => {
    const { data, error } = await supabase.rpc("rede_lua_student_constellation");
    if (error) dbError(error);
    return mapConstellation(data || {});
  },

  publicFeatureFlags: async (): Promise<Record<string, boolean>> => {
    const { data, error } = await supabase.from("rede_lua_feature_flags").select("key,enabled").eq("enabled", true);
    if (error) return {};
    return Object.fromEntries((data || []).map((row: any) => [row.key, Boolean(row.enabled)]));
  },
  activeAnnouncements: async (role?: Role | null): Promise<AdminAnnouncement[]> => {
    const { data, error } = await supabase.from("rede_lua_announcements")
      .select("id,title,body,audience,style,active,starts_at,ends_at,created_at")
      .order("created_at", { ascending: false });
    if (error) return [];
    return (data || []).filter((item: any) => item.active && (!item.starts_at || new Date(item.starts_at).getTime() <= Date.now()) && (!item.ends_at || new Date(item.ends_at).getTime() > Date.now()) && (item.audience === "all" || (role === "teacher" || role === "admin" ? item.audience === "teachers" : role === "student" ? item.audience === "students" : false))).map((item: any) => ({
      id:item.id,title:item.title,body:item.body||"",audience:item.audience,style:item.style,active:Boolean(item.active),startsAt:item.starts_at,endsAt:item.ends_at||null,createdAt:item.created_at,
    }));
  },
  mySecrets: async (): Promise<string[]> => {
    const { data, error } = await supabase.rpc("rede_lua_my_secrets");
    if (error) dbError(error);
    return Array.isArray(data) ? data : [];
  },
  claimSecret: async (secretId: string): Promise<{ ok:boolean; new:boolean; reward:number; coins:number }> => {
    const { data, error } = await supabase.rpc("rede_lua_claim_secret", { p_secret_id: secretId });
    if (error) dbError(error);
    return { ok:Boolean(data?.ok), new:Boolean(data?.new), reward:Number(data?.reward||0), coins:Number(data?.coins||0) };
  },

  adminDashboard: async (): Promise<AdminDashboard> => {
    const { data, error } = await supabase.rpc("rede_lua_admin_dashboard");
    if (error) dbError(error);
    return {
      users:Number(data?.users||0),students:Number(data?.students||0),teachers:Number(data?.teachers||0),admins:Number(data?.admins||0),suspended:Number(data?.suspended||0),activities:Number(data?.activities||0),gamesToday:Number(data?.gamesToday||0),answersToday:Number(data?.answersToday||0),activeAnnouncements:Number(data?.activeAnnouncements||0),
      recentUsers:(Array.isArray(data?.recentUsers)?data.recentUsers:[]).map((item:any)=>({ userId:item.userId,displayName:item.displayName,role:item.role,level:Number(item.level||1),status:item.status||"active",createdAt:item.createdAt })),
    };
  },
  adminUsers: async (query = ""): Promise<AdminUserRow[]> => {
    const { data, error } = await supabase.rpc("rede_lua_admin_users", { p_query: query || null, p_limit: 60 });
    if (error) dbError(error);
    return (Array.isArray(data)?data:[]).map((item:any)=>({ userId:item.user_id,displayName:item.display_name,email:item.email||"",role:item.role,level:Number(item.level||1),xp:Number(item.xp||0),moonCoins:Number(item.moon_coins||0),accountStatus:item.account_status||"active",createdAt:item.created_at }));
  },
  adminSetUserStatus: async (userId:string, status:"active"|"suspended", reason="") => {
    const { error } = await supabase.rpc("rede_lua_admin_set_user_status", { p_user_id:userId,p_status:status,p_reason:reason });
    if (error) dbError(error); return { ok:true as const };
  },
  adminSetUserRole: async (userId:string, role:"student"|"teacher") => {
    const { error } = await supabase.rpc("rede_lua_admin_set_user_role", { p_user_id:userId,p_role:role });
    if (error) dbError(error); return { ok:true as const };
  },
  adminTeacherInvites: async (): Promise<TeacherInviteAdmin[]> => {
    const { data, error } = await supabase.rpc("rede_lua_admin_teacher_invites");
    if (error) dbError(error);
    return (Array.isArray(data)?data:[]).map((item:any)=>({ id:item.id,label:item.label,active:Boolean(item.active),maxUses:Number(item.max_uses||0),uses:Number(item.uses||0),expiresAt:item.expires_at||null,createdAt:item.created_at }));
  },
  adminCreateTeacherInvite: async (payload:{label:string;code:string;maxUses:number;expiresAt?:string|null}) => {
    const { data, error } = await supabase.rpc("rede_lua_admin_create_teacher_invite", { p_label:payload.label,p_code:payload.code,p_max_uses:payload.maxUses,p_expires_at:payload.expiresAt||null });
    if (error) dbError(error); return data as {id:string;code:string};
  },
  adminAnnouncements: async (): Promise<AdminAnnouncement[]> => {
    const { data, error } = await supabase.rpc("rede_lua_admin_announcements");
    if (error) dbError(error);
    return (Array.isArray(data)?data:[]).map((item:any)=>({ id:item.id,title:item.title,body:item.body||"",audience:item.audience,style:item.style,active:Boolean(item.active),startsAt:item.starts_at,endsAt:item.ends_at||null,createdAt:item.created_at }));
  },
  adminSaveAnnouncement: async (payload:{id?:string|null;title:string;body:string;audience:AdminAnnouncement["audience"];style:AdminAnnouncement["style"];active:boolean;startsAt?:string|null;endsAt?:string|null}) => {
    const { data, error } = await supabase.rpc("rede_lua_admin_save_announcement", { p_id:payload.id||null,p_title:payload.title,p_body:payload.body,p_audience:payload.audience,p_style:payload.style,p_active:payload.active,p_starts_at:payload.startsAt||new Date().toISOString(),p_ends_at:payload.endsAt||null });
    if (error) dbError(error); return { id:String(data) };
  },
  adminFlags: async (): Promise<FeatureFlag[]> => {
    const { data, error } = await supabase.rpc("rede_lua_admin_flags");
    if (error) dbError(error);
    return (Array.isArray(data)?data:[]).map((item:any)=>({ key:item.key,enabled:Boolean(item.enabled),description:item.description||"",config:item.config||{},updatedAt:item.updated_at }));
  },
  adminSetFlag: async (key:string, enabled:boolean, config:Record<string,unknown>={}) => {
    const { error } = await supabase.rpc("rede_lua_admin_set_flag", { p_key:key,p_enabled:enabled,p_config:config });
    if (error) dbError(error); return { ok:true as const };
  },
  cosmeticCatalog: async (): Promise<CosmeticCatalogItem[]> => {
    const { data, error } = await supabase.rpc("rede_lua_cosmetic_catalog");
    if (error) dbError(error);
    return (Array.isArray(data)?data:[]).map((item:any)=>({ id:item.id,label:item.label,note:item.note||"",kind:item.kind,value:item.value,cost:Number(item.cost||0),level:Number(item.min_level||1),emoji:item.emoji||"✨",active:true }));
  },
  adminCosmetics: async (): Promise<CosmeticCatalogItem[]> => {
    const { data, error } = await supabase.rpc("rede_lua_admin_cosmetics");
    if (error) dbError(error);
    return (Array.isArray(data)?data:[]).map((item:any)=>({ id:item.id,label:item.label,note:item.note||"",kind:item.kind,value:item.value,cost:Number(item.cost||0),level:Number(item.min_level||1),emoji:item.emoji||"✨",active:Boolean(item.active) }));
  },
  adminUpdateCosmetic: async (payload:{id:string;cost:number;level:number;active:boolean}) => {
    const { error } = await supabase.rpc("rede_lua_admin_update_cosmetic", { p_id:payload.id,p_cost:payload.cost,p_min_level:payload.level,p_active:payload.active });
    if (error) dbError(error); return { ok:true as const };
  },
  adminActivities: async (query=""): Promise<AdminActivityRow[]> => {
    const { data, error } = await supabase.rpc("rede_lua_admin_activities", { p_query:query||null,p_limit:80 });
    if (error) dbError(error);
    return (Array.isArray(data)?data:[]).map((item:any)=>({ id:item.id,title:item.title,subject:item.subject,status:item.status,authorId:item.author_id,authorName:item.author_name||"Professor",games:Number(item.games||0),questions:Number(item.questions||0),createdAt:item.created_at }));
  },
  adminArchiveActivity: async (activityId:string, archived=true) => {
    const { error } = await supabase.rpc("rede_lua_admin_archive_activity", { p_activity_id:activityId,p_archived:archived });
    if (error) dbError(error); return { ok:true as const };
  },
  adminAuditLog: async (): Promise<AdminAuditEntry[]> => {
    const { data, error } = await supabase.rpc("rede_lua_admin_audit_log", { p_limit:80 });
    if (error) dbError(error);
    return (Array.isArray(data)?data:[]).map((item:any)=>({ id:Number(item.id),adminId:item.admin_id,adminName:item.admin_name||"Admin Rede Lua",action:item.action,targetType:item.target_type,targetId:item.target_id,details:item.details||{},createdAt:item.created_at }));
  },
  adminContactMessages: async (): Promise<AdminContactMessage[]> => {
    const { data, error } = await supabase.rpc("rede_lua_admin_contact_messages", { p_limit:100 });
    if (error) dbError(error);
    return (Array.isArray(data)?data:[]).map((item:any)=>({ id:item.id,name:item.name,email:item.email,topic:item.topic,message:item.message,status:item.status,createdAt:item.createdAt||item.created_at }));
  },

  recentStudies: async (): Promise<Array<{id:string;title:string;current_index:number}>> => {
    const {data,error}=await supabase.rpc("rede_lua_recent_studies");
    if(error) dbError(error);
    return data || [];
  },
  studyStart: async (activityId: string): Promise<StudyState> => {
    const { data, error } = await supabase.rpc("rede_lua_study_start", { p_activity_id: activityId });
    if (error) dbError(error);
    trackEvent("learning", "study_start", activityId);
    return data as StudyState;
  },
  studyState: async (sessionId: string): Promise<StudyState> => {
    const { data, error } = await supabase.rpc("rede_lua_study_state", { p_session_id: sessionId });
    if (error) dbError(error);
    return data as StudyState;
  },
  studyAttempt: async (sessionId: string, choiceIndex: number, reflection = ""): Promise<StudyAttemptResult> => {
    const { data, error } = await supabase.rpc("rede_lua_study_attempt", { p_session_id: sessionId, p_choice_index: choiceIndex, p_reflection: reflection });
    if (error) dbError(error);
    trackEvent("learning", "study_attempt", data?.correct ? "correct" : "retry");
    return data as StudyAttemptResult;
  },
  studySkip: async (sessionId: string): Promise<StudyState> => {
    const { data, error } = await supabase.rpc("rede_lua_study_skip", { p_session_id: sessionId });
    if (error) dbError(error);
    return data as StudyState;
  },
  setProfileHandle: async (handle: string) => {
    const { data, error } = await supabase.rpc("rede_lua_set_profile_handle", { p_handle: handle });
    if (error) dbError(error);
    return { handle: String(data) };
  },
  luaIdManifest: async (): Promise<LuaIdManifest> => {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!token) throw new ApiError("Entre para abrir seu LuaID.", 401, "AUTH_REQUIRED");
    return request<LuaIdManifest>("/api/luaid/me", { headers: { Authorization: `Bearer ${token}` } });
  },
  publicLuaId: async (handle: string) => request<LuaIdManifest>(`/api/luaid/profile/${encodeURIComponent(handle)}`),
  contact: async (payload: ContactPayload) => {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    return request<{ ok: true; stored: boolean; emailed: boolean }>("/api/contact", {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body: JSON.stringify(payload),
    });
  },


  recordsProfile: async (): Promise<RecordsProfile> => {
    const { data, error } = await supabase.rpc("rede_lua_my_records");
    if (error) dbError(error);
    const life = data?.life || {};
    const summary = data?.summary || {};
    return {
      life: {
        number: Number(life.number || 1), xp: Number(life.xp || 0), level: Number(life.level || 1),
        nextLifeXp: Number(life.nextLifeXp || 600), ready: Boolean(life.ready),
        legacyStars: Number(life.legacyStars || 0), lastRebirthAt: life.lastRebirthAt || null,
      },
      summary: {
        gamesCompleted: Number(summary.gamesCompleted || 0), answers: Number(summary.answers || 0),
        correct: Number(summary.correct || 0), accuracy: Number(summary.accuracy || 0),
        bestGameScore: Number(summary.bestGameScore || 0), largestRoom: Number(summary.largestRoom || 0),
      },
      records: (Array.isArray(data?.records) ? data.records : []).map((item:any)=>({
        key:String(item.key), label:String(item.label || item.key), value:Number(item.value || 0), meta:item.meta || {}, achievedAt:item.achievedAt || item.achieved_at || new Date().toISOString(),
      })),
      history: (Array.isArray(data?.history) ? data.history : []).map((item:any)=>({
        id:String(item.id), lifeNumber:Number(item.lifeNumber || 1), finalLifeXp:Number(item.finalLifeXp || 0),
        legacyStarsEarned:Number(item.legacyStarsEarned || 0), avatarSnapshot:item.avatarSnapshot || {},
        recordSnapshot:item.recordSnapshot || {}, endedAt:item.endedAt || new Date().toISOString(),
      })),
    };
  },
  beginNewLife: async (): Promise<NewLifeResult> => {
    const { data, error } = await supabase.rpc("rede_lua_begin_new_life");
    if (error) dbError(error);
    trackEvent("profile", "new_life", String(data?.lifeNumber || ""));
    return data as NewLifeResult;
  },

  myNotifications: async (): Promise<UserNotification[]> => {
    const data = await request<{ notifications: UserNotification[] }>("/api/notifications/me", { headers: await authHeaders() });
    return Array.isArray(data.notifications) ? data.notifications : [];
  },
  notificationPing: async (id: string) => request<{ id: string; seen: boolean; show?: boolean; timesToShow: number; timesShown: number; lastShownAt: string | null }>(`/api/notifications/${encodeURIComponent(id)}/ping`, { method: "POST", headers: await authHeaders() }),
  notificationAck: async (id: string) => request<{ id: string; seen: boolean; timesToShow: number; timesShown: number; lastShownAt: string | null }>(`/api/notifications/${encodeURIComponent(id)}/ack`, { method: "POST", headers: await authHeaders() }),

  books: (q: string) => request<{ books: Array<{ key: string; title: string; authors: string[]; year: number | null; coverUrl: string | null }> }>(`/api/discover/books?q=${encodeURIComponent(q)}`),
  wiki: (q: string) => request<{ results: Array<{ title: string; extract: string; thumbnail: string | null; url: string | null }> }>(`/api/discover/wiki?q=${encodeURIComponent(q)}`),
  states: () => request<{ states: Array<{ id: number; sigla: string; nome: string; regiao: string }> }>("/api/discover/ibge/states"),
  weather: (city: string) => request<{ place: null | { name: string; admin1: string; country: string; latitude: number; longitude: number }; current: any; daily: Array<{ date: string; max: number; min: number; rainChance: number }> }>(`/api/discover/weather?city=${encodeURIComponent(city)}`),
  world: (country: string, metric: string) => request<{ country: string; metric: { key: string; label: string; unit: string }; points: Array<{ year: string; value: number }> }>(`/api/discover/world?country=${encodeURIComponent(country)}&metric=${encodeURIComponent(metric)}`),
};

export { ApiError };
